import { useEffect, useState } from "react";

import {
  CardActions,
  CardContent,
  IconButton,
  Card,
  Box,
  Button,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataText, CircularLoading, FadeInContainer } from "../../../Atoms";
import useAuth from "../../../../../hooks/context/useAuth";
import { useTranslate } from "../../../../../hooks/useTranslate";
import useToast from "../../../../../hooks/context/useToast";
import {
  createAddress,
  deleteAddress,
  editAddress,
  getUserAddress,
} from "../../../../../services/addressService";
import { CustomModal } from "../../Modal";
import { useBreakpoints } from "../../../../../hooks/useBreakpoins";
import { EditCreateAddress } from "../EditAddress";
import { colors } from "../../../../../theme/colors";

interface Props {
  userData: User;
}

export function AddressBox({ userData }: Props) {
  const { setOpen, setToastData } = useToast();
  const { user, language } = useAuth();
  const { strings } = useTranslate();
  const { isSmalldesktop } = useBreakpoints();
  const [loading, setLoading] = useState(false);
  const [loadingNewAd, setLoadingNewAd] = useState(false);
  const [address, setAddress] = useState<IAddress[]>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>();

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAddress(null);
  };

  const myAddress = async (id: number) => {
    setLoading(true);
    try {
      const myAddress = await getUserAddress(id);
      setAddress(myAddress);
    } catch (err) {
      setToastData({
        anchor: { vertical: "top", horizontal: "center" },
        duration: 3000,
        type: "error",
        message: strings.toastMessages.errorAddress[language],
      });
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const createEditAddress = async (val: EditCreateAddress) => {
    setLoadingNewAd(true);
    if (val.type === "create") {
      try {
        await createAddress(val);
        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "success",
          message: strings.toastMessages.successCreateAddress[language],
        });
        setOpen(true);
        await myAddress(user?.user?.id);
        closeModal();
      } catch (err) {
        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "error",
          message: strings.toastMessages.errorCreateAddress[language],
        });
        setOpen(true);
      } finally {
        setLoadingNewAd(false);
      }
    } else {
      try {
        await editAddress(val);
        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "success",
          message: strings.toastMessages.successUpdateAddress[language],
        });
        setOpen(true);
        await myAddress(user?.user?.id);
        closeModal();
      } catch (err) {
        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "error",
          message: strings.toastMessages.errorUpdateAddress[language],
        });
        setOpen(true);
      } finally {
        setLoadingNewAd(false);
      }
    }
  };

  const deleteMyAddress = async (addressID: number) => {
    try {
      await deleteAddress(addressID);
      setToastData({
        anchor: { vertical: "top", horizontal: "center" },
        duration: 5000,
        type: "success",
        message: strings.toastMessages.successDeleteAddress[language],
      });
      setOpen(true);
      await myAddress(user?.user?.id);
    } catch {
      setToastData({
        anchor: { vertical: "top", horizontal: "center" },
        duration: 5000,
        type: "error",
        message: strings.toastMessages.errorDeleteAddress[language],
      });
      setOpen(true);
    }
  };

  useEffect(() => {
    const localUser = localStorage.getItem("@treasurebox_user") as any;
    if (localUser) {
      const parseID = JSON.parse(localUser).user.id;

      myAddress(parseID);
    }
    // eslint-disable-next-line
  }, []);

  const titleWidth = isSmalldesktop ? 130 : 140;

  return (
    <Box
      width={"100%"}
      minHeight={"480px"}
      display="flex"
      flexWrap={"wrap"}
      justifyContent={"space-evenly"}
    >
      <Box borderRadius={"10px"} width={"100%"}>
        <Box
          borderBottom={"1px solid lightgray"}
          p={2}
          bgcolor={colors.purple[500]}
          borderRadius={"10px"}
          display="flex"
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography color={"#FFFFFF"} fontSize={18}>
            Meus endereços
          </Typography>
          <Button
            sx={{
              bgcolor: colors.purple[700],
              color: "white",
              "&:hover": { bgcolor: colors.purple[200] },
            }}
            onClick={() => setModalOpen(true)}
          >
            Novo endereço
          </Button>
        </Box>
      </Box>
      <FadeInContainer>
        <Box
          display={"flex"}
          width={"95%"}
          gap={1}
          p={1}
          flexWrap={"wrap"}
          mt={1}
          maxHeight={700}
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <CircularLoading />
          ) : (
            address &&
            address?.length > 0 &&
            address.map((item, index) => (
              <Card
                sx={{
                  width: "100%",
                  height: 300,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
                key={index}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                      justifyContent: "center",
                      rowGap: 1,
                    }}
                  >
                    <DataText
                      title={"Endereço"}
                      text={`${item.address} ${item.adressNumber}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"Complemento"}
                      text={`${item.addressComplement}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"Bairro"}
                      text={`${item.neighborhood}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"Cidade"}
                      text={`${item.city} / ${item.state}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"País"}
                      text={`${item.country}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"CEP"}
                      text={`${item.postalCode}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"CPF"}
                      text={`${item.ssn}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                    <DataText
                      title={"Recebedor"}
                      text={`${item.receiver}`}
                      titleProps={{ minWidth: titleWidth }}
                    />
                  </Box>
                </CardContent>
                <CardActions
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSelectedAddress(item);
                      setModalOpen(true);
                    }}
                    style={{
                      color: "white",

                      minHeight: 40,
                    }}
                  >
                    Editar
                  </Button>

                  <IconButton
                    onClick={() => deleteMyAddress(item.id)}
                    aria-label="delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          )}
        </Box>
      </FadeInContainer>
      <CustomModal
        openModal={modalOpen}
        setOpenModal={closeModal}
        width={isSmalldesktop ? "90%" : "50%"}
      >
        <EditCreateAddress
          userData={userData}
          submitLoading={loadingNewAd}
          address={selectedAddress}
          handleSubmit={(val) => createEditAddress(val)}
          thisUserAlreadyHaveAddress={
            address && address?.length > 0 ? true : false
          }
        />
      </CustomModal>
    </Box>
  );
}
